/**
 * SystemPlaylistFullView — full-page view for system playlists.
 *
 * Serves as the "See Entire Playlist" destination for:
 *   /watch-later        — Watch Later (playlistType='default')
 *   /favorites          — Favorites  (playlistType='favorites')
 *   /currently-watching  — In-progress series
 *   /watch-history       — Watch history
 *
 * Uses Apollo hooks for data fetching (no Redux tiles dependency).
 * Reuses CommunityMyLibraryPage's static text store key.
 */

import React, {
  useState, useMemo, useCallback, useEffect, useRef,
} from 'react'
import { useSelector } from 'react-redux'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import type { Reference } from '@apollo/client'
import { compose } from 'recompose'
import { usePostHogEvent } from '../../../../PostHog/index'
import { selectAuth } from '../../../../../services/auth/selectors'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import { StaticTextProvider, useTranslatedText } from '../../../../StaticText/StaticTextContext'
import { connect as connectPage, PAGE_SEO_TYPE_LOCATION } from '../../../../Page/connect'
import BackButton from '../../../../BackButton/index'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import { H2 } from '../../../../Heading/index'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../../../../Loading/index'
import Pencil from '../../../../Icon/icons/Pencil/index'
import Check from '../../../../Icon/icons/Check/index'
import { InfiniteScroll } from '../../../../OraclePage/InfiniteScroll'
import {
  PlaylistTileGrid,
  PlaylistGridSkeleton,
} from '../../shared/CommunityProfileCarousels/index'
import type {
  CarouselItem,
  EditToolbarLabels,
} from '../../shared/CommunityProfileCarousels/index'
import useCommunityEnabled from '../../../../../services/community/shared/useCommunityEnabled'
import useRequireAuth from '../../../../../services/community/shared/useRequireAuth'
import usePrimaryAppLanguage from '../../../../../services/community/shared/usePrimaryAppLanguage'
import useIsMobile from '../../../../../services/community/shared/useIsMobile'
import useInProgressEnrichment from '../../../../../services/community/identity/library/useInProgressEnrichment'
import useWatchHistoryData from '../../../../../services/community/identity/library/useWatchHistoryData'
import {
  GET_MY_PLAYLISTS,
  GET_IN_PROGRESS_FULL,
  REMOVE_PLAYLIST_ITEM,
  UPDATE_PLAYLIST_ITEM,
} from '../../../../../services/community/query'
import { FLAG_WATCHED_VIDEO } from '../../../../../services/playlist/query'
import {
  enrichPlaylistItems,
} from '../../../../../services/community/identity/library/content-utils'
import { createRemoveItemUpdater } from '../../../../../services/community/shared/cache-updaters'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../../../../services/apollo-graphql/index'

import type {
  GetMyPlaylistsResponse,
  GetInProgressFullResponse,
  Playlist,
  EnrichedPlaylistItem,
  EnrichedInProgressItem,
  StaticTextMap,
} from '../../../../../services/community/types'

// ---------------------------------------------------------------------------
// Route config — maps pathname to data source + i18n keys
// ---------------------------------------------------------------------------

type DataSource = 'playlist' | 'inProgress' | 'watchHistory'

interface RouteConfig {
  playlistType?: string
  /** Optional for custom playlists — title comes from playlistName instead */
  titleKey?: string
  emptyTextKey: string
  dataSource: DataSource
  /** Present when the pathname is `/playlist/<uuid>` (custom playlist full view) */
  customPlaylistId?: string
}

const ROUTE_CONFIGS: Record<string, RouteConfig> = {
  '/watch-later': {
    playlistType: 'default',
    titleKey: 'watchLater',
    emptyTextKey: 'emptyWatchLater',
    dataSource: 'playlist',
  },
  '/favorites': {
    playlistType: 'favorites',
    titleKey: 'favorites',
    emptyTextKey: 'emptyFavorites',
    dataSource: 'playlist',
  },
  '/currently-watching': {
    titleKey: 'currentlyWatching',
    emptyTextKey: 'emptyCurrentlyWatching',
    dataSource: 'inProgress',
  },
  '/watch-history': {
    titleKey: 'watchHistory',
    emptyTextKey: 'emptyWatchHistory',
    dataSource: 'watchHistory',
  },
}

/** Matches `/playlist/<uuid>` — the custom playlist full-view route.
 *  Case-insensitive to align with path-to-regexp's default route matching
 *  (see `lib/services/resolver/match-path.js`). */
const CUSTOM_PLAYLIST_PATH_RE = /^\/playlist\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i

function getRouteConfig (pathname: string): RouteConfig {
  const customMatch = pathname.match(CUSTOM_PLAYLIST_PATH_RE)
  if (customMatch) {
    return {
      dataSource: 'playlist',
      emptyTextKey: 'noEpisodesYet',
      customPlaylistId: customMatch[1],
    }
  }
  return ROUTE_CONFIGS[pathname] || ROUTE_CONFIGS['/watch-later']
}

// ---------------------------------------------------------------------------
// Watch history page size for load-more
// ---------------------------------------------------------------------------

const WATCH_HISTORY_PAGE_SIZE = 24

// ---------------------------------------------------------------------------
// Inner component (receives staticText from HOC)
// ---------------------------------------------------------------------------

interface SystemPlaylistFullViewInnerProps {
  location: { pathname: string }
  history: { push: (path: string) => void }
  staticText: StaticTextMap
}

// Inner component: lives inside the Outer's <StaticTextProvider> (set up by
// SystemPlaylistFullViewContent below). The `staticText` prop is no longer
// passed in — UI copy is resolved via useTranslatedText hooks against the
// surrounding provider.
function SystemPlaylistFullViewContentInner ({
  location,
}: Omit<SystemPlaylistFullViewInnerProps, 'staticText'>) {
  const { enabled, loading: communityLoading } = useCommunityEnabled()
  const { isLoggedIn } = useRequireAuth()
  const language = usePrimaryAppLanguage()
  const auth = useSelector(selectAuth)
  const jwt = (auth.get('jwt') as string) || ''
  const viewerUuid = (auth.get('uuid') as string) || ''
  const apolloClient = useApolloClient()

  const [editMode, setEditMode] = useState(false)
  const handleToggleEditMode = useCallback(() => {
    setEditMode((prev) => !prev)
  }, [])

  // Toolbar size scales with the design system's primary breakpoints so the
  // pills + icons match the surrounding tile size at each viewport tier:
  //   < 768px (Mobile)  → small  (26×26 / 18px) — same as carousel-row default
  //   768–1279 (Tablet) → medium (32×32 / 22px)
  //   ≥ 1280  (Desktop) → large  (36×36 / 24px)
  // Inline icon `size` is set via React prop (no CSS-`!important` workaround
  // exists), so we drive the variant from JS via two `useIsMobile` calls.
  const isMobileBp = useIsMobile(768) // < 768px
  const isTabletOrBelow = useIsMobile(1280) // < 1280px
  let toolbarSize: 'small' | 'medium' | 'large' = 'large'
  if (isMobileBp) toolbarSize = 'small'
  else if (isTabletOrBelow) toolbarSize = 'medium'

  // Optimistic local order while a reorder mutation is in flight.  Without
  // this the user clicks Move Earlier/Later and sees no visual change until
  // the network round-trip finishes — and even then only if `enrichPlaylistItems`
  // sorts items by `playlistPosition` (which it does as of this fix).  The
  // optimistic state gives instant feedback; we clear it after refetch so
  // the server data takes over.
  const [optimisticItems, setOptimisticItems] = useState<EnrichedPlaylistItem[] | null>(null)

  // Lock all reorder chevrons across the playlist while a single move is
  // mutating + refetching.  Without this lock, rapid clicks queue multiple
  // mutations against stale `playlistPosition` values and the mutations may
  // resolve out of order — final server state ends up not matching either
  // the user's intent or the optimistic UI.
  const [reorderInFlight, setReorderInFlight] = useState(false)

  // Drop the optimistic order whenever the user navigates to a different
  // playlist or leaves edit mode — those signals indicate the local
  // working state is no longer valid.
  useEffect(() => {
    setOptimisticItems(null)
  }, [location.pathname, editMode])

  const config = getRouteConfig(location.pathname)
  const isPlaylistRoute = config.dataSource === 'playlist'
  const isInProgressRoute = config.dataSource === 'inProgress'
  const isWatchHistoryRoute = config.dataSource === 'watchHistory'

  // ── Playlist data (Watch Later / Favorites) ─────────────────────────
  const {
    data: playlistsData,
    loading: playlistsLoading,
    refetch: refetchPlaylists,
  } = useQuery<GetMyPlaylistsResponse>(GET_MY_PLAYLISTS, {
    variables: { language: [language, '_primary'] },
    skip: !isPlaylistRoute || !isLoggedIn,
    // cache-and-network so a navigation from My Playlists → /watch-later (or
    // /favorites or /playlist/<uuid>) paints the just-updated visibility from
    // cache instead of waiting for a fresh server response that may still
    // carry the stale value.  The UPDATE_PLAYLIST_VISIBILITY mutation in
    // useProfilePlaylists writes an explicit cache.modify on
    // Playlist:<id>.visibility to make this work.
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  const targetPlaylist = useMemo(() => {
    if (!isPlaylistRoute || !playlistsData) return null
    const playlists = playlistsData.playlistList?.playlists || []
    if (config.customPlaylistId) {
      return playlists.find(
        (pl: Playlist) => pl.id === config.customPlaylistId,
      ) || null
    }
    return playlists.find(
      (pl: Playlist) => pl.playlistType === config.playlistType,
    ) || null
  }, [isPlaylistRoute, playlistsData, config.playlistType, config.customPlaylistId])

  const playlistItems = useMemo(() => {
    if (!targetPlaylist) return []
    // Uniform behavior across all playlist routes (`/watch-later`,
    // `/favorites`, `/playlist/<uuid>`): render every stored item as its
    // own episode tile, with no series-pivot and no dedupe. Watch Later
    // and Favorites previously mimicked the "Continue Watching" collapse
    // pattern; that was walked back so the rendered list always matches
    // the playlist's stored contents.
    return enrichPlaylistItems(targetPlaylist.playlistItems || [], {
      dedupe: false,
      pivotEpisodesToSeries: false,
    })
  }, [targetPlaylist])

  const [removePlaylistItemMutation] = useMutation(REMOVE_PLAYLIST_ITEM, {
    context: { jwt },
  })

  const [updatePlaylistItemMutation] = useMutation(UPDATE_PLAYLIST_ITEM, {
    context: { jwt },
  })

  const handleRemovePlaylistItem = useCallback(async (item: CarouselItem) => {
    if (!targetPlaylist) return
    const enrichedItem = item as EnrichedPlaylistItem
    // Use the playlist-item ROW's own contentId / contentType — these are
    // the values the backend matches against. Reading from the tile or the
    // resolved content fails for series-pivoted tiles (`'series'`) and
    // gaia originals (`'gaiaOriginal'`), neither of which matches the
    // row's stored `content_type`.
    const contentId = enrichedItem.playlistItemContentId
    const contentType = enrichedItem.playlistItemContentType || 'video'
    const playlistItemId = enrichedItem.id
    if (!contentId || !playlistItemId) return
    try {
      // Cache-correct remove via the shared updater: filters the targeted
      // Playlist entity's `playlistItems` array and decrements `itemCount`
      // in the same modify() pass.  Preserves scroll position past page 1
      // and avoids the multi-refetch amplification a multi-select would
      // otherwise produce.  Reorder still uses refetchPlaylists() because
      // server ordering is authoritative.
      await removePlaylistItemMutation({
        variables: {
          contentId,
          contentType,
          playlistId: targetPlaylist.id,
          playlistType: targetPlaylist.playlistType,
        },
        update: (cache) => {
          createRemoveItemUpdater({
            entityId: cache.identify({ __typename: 'Playlist', id: targetPlaylist.id }),
            field: 'playlistItems',
            countField: 'itemCount',
            countLocation: 'sibling',
            match: (ref, readField) => String(readField('id', ref)) === String(playlistItemId),
          })(cache)
        },
      })
    } catch {
      // mutation failed — item stays visible
    }
  }, [targetPlaylist, removePlaylistItemMutation, apolloClient])

  // Move Earlier / Move Later handler.  PlaylistTileGrid forwards the click
  // through `onReorderItem(fromIndex, toIndex, draggedItemId)`; we look up
  // the moving + destination items in our local list and fire the mutation.
  //
  // Three-step UX:
  //   1. Optimistic local swap so the tile visibly moves on click — no
  //      waiting for the network round-trip.
  //   2. Fire the mutation; the Brooklyn resolver requires the row's
  //      contentId/contentType (same pair the delete mutation needs) —
  //      without these the mutation 500s with `"contentId" is required`.
  //   3. On success: refetch and clear the optimistic state so server data
  //      takes over.  On failure: clear optimistic state to revert.
  const handleReorderPlaylistItem = useCallback(async (
    fromIndex: number,
    toIndex: number,
  ) => {
    if (!targetPlaylist || fromIndex === toIndex) return
    // Defense in depth — the UI also disables the chevrons via reorderInFlight,
    // but a stale event (touchstart racing the disabled-state commit) could
    // still slip through.  Drop it.
    if (reorderInFlight) return
    // Read from the current visible list (optimistic if set, else server).
    const currentList = optimisticItems ?? playlistItems
    const moving = currentList[fromIndex] as EnrichedPlaylistItem | undefined
    const dest = currentList[toIndex] as EnrichedPlaylistItem | undefined
    if (!moving || !dest) return
    const targetPosition = dest.playlistPosition
    const contentId = moving.playlistItemContentId
    const contentType = moving.playlistItemContentType || 'video'
    if (typeof targetPosition !== 'number' || !contentId) return

    // Step 1 — optimistic swap so the tile visibly moves on click.
    const next = [...currentList]
    next.splice(fromIndex, 1)
    next.splice(toIndex, 0, moving)
    setOptimisticItems(next)
    setReorderInFlight(true)

    try {
      // Step 2 — fire the mutation.
      await updatePlaylistItemMutation({
        variables: {
          playlistItemId: moving.id,
          contentId,
          contentType,
          playlistPosition: targetPosition,
          playlistId: targetPlaylist.id,
          playlistType: targetPlaylist.playlistType,
        },
      })
      // Step 3a — refetch, then yield to server data (enrichPlaylistItems
      // sorts by playlistPosition DESC so the order will be correct).
      await refetchPlaylists()
      setOptimisticItems(null)
    } catch {
      // Step 3b — revert: drop optimistic state and let the server data show.
      setOptimisticItems(null)
    } finally {
      // Always release the lockout so the user can move again.  This runs
      // after both the success and the catch paths.
      setReorderInFlight(false)
    }
  }, [
    targetPlaylist,
    playlistItems,
    optimisticItems,
    reorderInFlight,
    updatePlaylistItemMutation,
    refetchPlaylists,
  ])

  // ── In-progress data (Currently Watching) ───────────────────────────
  // Uses the inProgress federated field (same data source as the profile
  // carousel row) with limit 24. This field includes hiddenFromContinue
  // which useInProgressEnrichment needs for proper client-side filtering.
  const {
    data: inProgressData,
    loading: inProgressLoading,
  } = useQuery<GetInProgressFullResponse>(GET_IN_PROGRESS_FULL, {
    variables: { language, contentLanguage: [language, '_primary'] },
    skip: !isInProgressRoute || !isLoggedIn,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  const rawInProgress = isInProgressRoute
    ? inProgressData?.userProfileCurrent?.inProgress
    : undefined
  // language + jwt thread into useInProgressEnrichment's internal
  // useContentHydration call so the difficulty pill renders on this
  // surface — PR 2 dropped `featureMedia.mediaAssets.aiMetadata.learnerLevel`
  // from GET_IN_PROGRESS_FULL; hydration restores it.
  // The hook returns `{ items, isHydrating }` — this full-view surface
  // doesn't surface skeleton state today (the page-level loading gates the
  // shell), so we only consume `items`. Add `isHydrating` here if/when
  // this view grows a per-row skeleton path.
  const { items: inProgressItems } = useInProgressEnrichment(rawInProgress, language, jwt)

  const [flagWatchedVideoMutation] = useMutation(FLAG_WATCHED_VIDEO, {
    context: { jwt },
  })

  const handleRemoveInProgressItem = useCallback(async (item: CarouselItem) => {
    // Bundle fan-out: a series-pivoted Currently Watching tile collapses N
    // raw in-progress episode rows into one visible tile (see
    // `useInProgressEnrichment.ts:97-207`).  Removing must hide EVERY
    // bundled episode — both server-side (N `flagWatchedVideo` mutations)
    // and in the cache (drop every matching `UserProfile.inProgress`
    // entry) — otherwise the leftover episodes re-pivot into the same
    // series tile on the next render and the user sees Remove fail to
    // stick.  Standalone videos arrive with a single-element bundle, so
    // the same code path covers both shapes uniformly.
    const enriched = item as EnrichedInProgressItem
    const fallbackContentId = Number(item.contentId)
    const enrichedContentIds = enriched.bundledEpisodeContentIds
    let bundleContentIds: number[]
    if (enrichedContentIds && enrichedContentIds.length > 0) {
      bundleContentIds = enrichedContentIds.filter((id) => Number.isFinite(id) && id > 0)
    } else if (Number.isFinite(fallbackContentId) && fallbackContentId > 0) {
      bundleContentIds = [fallbackContentId]
    } else {
      bundleContentIds = []
    }
    const fallbackMediaId = enriched.mediaId ?? enriched.id
    const enrichedMediaIds = enriched.bundledEpisodeMediaIds
    let bundleMediaIds: string[]
    if (enrichedMediaIds && enrichedMediaIds.length > 0) {
      bundleMediaIds = enrichedMediaIds
    } else if (fallbackMediaId != null) {
      bundleMediaIds = [String(fallbackMediaId)]
    } else {
      bundleMediaIds = []
    }
    if (bundleContentIds.length === 0) return

    // Fire one flagWatchedVideo per bundled episode in parallel.
    // `Promise.allSettled` so a single resolver hiccup on a tail episode
    // doesn't strand the rest — the user expects the Remove click to
    // make a best-effort attempt at hiding everything.
    const results = await Promise.allSettled(
      bundleContentIds.map((videoId) => flagWatchedVideoMutation({
        variables: { videoId, hiddenByUser: true },
      })),
    )
    const succeeded = results.filter((r) => r.status === 'fulfilled').length
    // Every mutation rejected → keep the tile visible (fail-quiet contract
    // matching the prior single-mutation behavior). Partial success still
    // commits the cache.modify so the user's optimistic UI reflects what
    // did succeed.
    if (succeeded === 0) return

    if (!viewerUuid) return
    const profileId = apolloClient.cache.identify({ __typename: 'UserProfile', uuid: viewerUuid })
    if (!profileId) return
    const bundleMediaIdSet = new Set(bundleMediaIds.map(String))
    apolloClient.cache.modify({
      id: profileId,
      fields: {
        inProgress (existing, { readField }) {
          if (!Array.isArray(existing)) return existing
          const list = existing as readonly Reference[]
          return list.filter(
            (entry) => !bundleMediaIdSet.has(String(readField('mediaId', entry))),
          )
        },
      },
    })
  }, [flagWatchedVideoMutation, viewerUuid, apolloClient])

  // ── Watch History data ──────────────────────────────────────────────
  const {
    watchHistoryItems,
    watchHistoryLoading,
    onRemoveWatchHistoryItem,
    loadMore,
    lastBatchSize,
    isLoadingMore,
  } = useWatchHistoryData({
    language,
    jwt,
    isLoggedIn: isLoggedIn && isWatchHistoryRoute,
    limit: WATCH_HISTORY_PAGE_SIZE,
  })

  const handleRemoveWatchHistoryItem = useCallback(async (item: CarouselItem) => {
    await onRemoveWatchHistoryItem(item as EnrichedPlaylistItem)
  }, [onRemoveWatchHistoryItem])

  // ── Page-view tracking (PostHog session-recording trigger) ─────────────
  // Fires once after the route resolves and (for playlist routes) the data
  // query settles enough to expose a stable identifier.  A ref guard makes
  // this resilient to React strict-mode and Apollo cache updates.
  const trackPlaylistViewed = usePostHogEvent('community_playlist_viewed')
  const hasTrackedPlaylistView = useRef(false)
  const playlistId = config.customPlaylistId
    || config.playlistType
    || config.dataSource
  const playlistType: 'custom' | 'system' = config.customPlaylistId ? 'custom' : 'system'
  // System playlists are always the viewer's own; custom playlists shown on
  // this route come from GET_MY_PLAYLISTS so they're owned by the viewer when
  // `targetPlaylist` resolves.
  const isOwner = playlistType === 'system'
    ? true
    : !!targetPlaylist
  useEffect(() => {
    if (hasTrackedPlaylistView.current) return
    if (!isLoggedIn) return
    if (!playlistId) return
    // For custom playlists wait until the lookup settles so we don't fire
    // with an unresolved owner state.
    if (playlistType === 'custom' && playlistsLoading) return
    hasTrackedPlaylistView.current = true
    trackPlaylistViewed(playlistId, playlistType, isOwner)
  }, [isLoggedIn, playlistId, playlistType, isOwner, playlistsLoading, trackPlaylistViewed])

  // ── Unified items + loading + remove handler ────────────────────────
  let items: CarouselItem[] = []
  let dataLoading = false
  // Returns void | Promise<void>: the carousel awaits this so it can show a
  // ghost-tile placeholder until the mutation+refetch resolves.
  let onRemoveItem: ((item: CarouselItem) => void | Promise<void>) | undefined

  if (isPlaylistRoute) {
    // Prefer the optimistic local order if a reorder is in flight; falls
    // back to the server-sorted enriched items otherwise.
    items = optimisticItems ?? playlistItems
    dataLoading = playlistsLoading
    onRemoveItem = handleRemovePlaylistItem
  } else if (isInProgressRoute) {
    items = inProgressItems
    dataLoading = inProgressLoading
    onRemoveItem = handleRemoveInProgressItem
  } else if (isWatchHistoryRoute) {
    items = watchHistoryItems
    dataLoading = watchHistoryLoading
    onRemoveItem = handleRemoveWatchHistoryItem
  }

  // ── Gate checks ─────────────────────────────────────────────────────
  if (communityLoading) {
    return <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
  }
  if (!enabled || !isLoggedIn) {
    return null
  }

  // ── i18n strings ────────────────────────────────────────────────────
  // Hooks resolve against the surrounding <StaticTextProvider> installed by
  // the Outer wrapper (SystemPlaylistFullViewContent below). Title and empty-
  // text keys are dynamic per route — the hook accepts a derived key string
  // just fine because each call site is a single, stable hook invocation.
  const playlistFallbackLabel = useTranslatedText({ keys: ['playlist'] })
  const titleFromKey = useTranslatedText({ keys: [config.titleKey || ''] })
  const emptyText = useTranslatedText({ keys: [config.emptyTextKey] })
  const backLabel = useTranslatedText({ keys: ['backToLibrary'] })
  const editPlaylistLabel = useTranslatedText({ keys: ['editPlaylistLabel'] })
  const doneEditingLabel = useTranslatedText({ keys: ['doneEditingLabel'] })
  const earlierLabel = useTranslatedText({ keys: ['moveEarlierInPlaylist'] })
  const laterLabel = useTranslatedText({ keys: ['moveLaterInPlaylist'] })
  const removeLabel = useTranslatedText({ keys: ['removeFromPlaylist'] })
  const confirmLabel = useTranslatedText({ keys: ['confirmRemoveFromPlaylist'] })

  // Custom playlists render their user-authored name; system playlists use
  // a fixed staticText title keyed by pathname.
  let title: string
  if (config.customPlaylistId) {
    title = targetPlaylist?.playlistName || playlistFallbackLabel
  } else {
    title = titleFromKey
  }

  // Edit-toolbar i18n labels resolved once at the parent and threaded down
  // to every reorder/remove pill in the grid.
  const editToolbarLabels: EditToolbarLabels = {
    earlierLabel,
    laterLabel,
    removeLabel,
    confirmLabel,
  }

  const backUrl = '/playlist/default'

  return (
    <div className="system-playlist-full-view">
        <div className="system-playlist-full-view__header">
          <div className="system-playlist-full-view__back">
            <BackButton
              className="system-playlist-full-view__back-button"
              url={backUrl}
            >
              {backLabel}
            </BackButton>
          </div>
          <H2 className="system-playlist-full-view__title">{title}</H2>
          <div className="system-playlist-full-view__edit-action">
            {items.length > 0 ? (
              <Button
                type={BUTTON_TYPES.GHOST}
                className="system-playlist-full-view__edit-button"
                onClick={handleToggleEditMode}
                aria-label={editMode ? doneEditingLabel : editPlaylistLabel}
              >
                {editMode ? <Check /> : <Pencil />}
              </Button>
            ) : null}
          </div>
        </div>
        {dataLoading && items.length === 0 ? (
          // Tile-shaped skeleton instead of the generic spinner so the
          // transition into <PlaylistTileGrid> is reflow-free.  Eight
          // cells fill the visible viewport at the most common widths
          // (4 × 2 desktop, 3 × ~3 tablet, 2 × 4 mobile).  The full view
          // is grid-only at every viewport, so we use the grid skeleton
          // directly rather than the responsive carousel one.
          //
          // Series-label corner-badge placeholder is opt-in via
          // `showSeriesLabel` and only enabled on the Currently Watching
          // full view (`isInProgressRoute`), where almost every tile is a
          // series rollup.  Watch Later / Favorites / Watch History /
          // custom playlists rarely render series labels, so we keep the
          // skeleton badge-free on those routes to avoid the flash-then-
          // disappear on hand-off.
          <PlaylistGridSkeleton count={8} showSeriesLabel={isInProgressRoute} />
        ) : null}
        {items.length > 0 ? (
          <PlaylistTileGrid
            items={items}
            initialCount={items.length}
            seeMoreLabel=""
            hideToggle
            editMode={editMode}
            onRemoveItem={onRemoveItem}
            // Reorder is only meaningful on owned-playlist routes — Currently
            // Watching and Watch History are server-ordered, not user-orderable.
            onReorderItem={isPlaylistRoute ? handleReorderPlaylistItem : undefined}
            // Lock all reorder chevrons while a single move is mutating +
            // refetching.  Prevents users from queueing multiple moves whose
            // mutations would race against stale `playlistPosition` values.
            reorderDisabled={reorderInFlight}
            // Toolbar pill + icon size scales with the viewport: small on mobile,
            // medium on tablet, large on desktop.  Computed at the top of this
            // component from `useIsMobile` against 768/1280 breakpoints.
            editToolbarSize={toolbarSize}
            editToolbarLabels={editToolbarLabels}
          />
        ) : null}
        {isWatchHistoryRoute ? (
          <>
            <InfiniteScroll
              onLoadMore={loadMore}
              currentCount={items.length}
              lastBatchSize={lastBatchSize}
              limit={WATCH_HISTORY_PAGE_SIZE}
              loading={isLoadingMore}
            />
            {/*
              InfiniteScroll itself renders only a hidden intersection-observer
              div — no visible affordance.  Surface an explicit Loading spinner
              while the fetchMore round-trip (200–800ms) is in flight so a user
              scrolling past page 1 doesn't conclude the list is exhausted.
              `data-infinite-scroll-spinner` is a regression-guard probe.
            */}
            {isLoadingMore ? (
              <div
                className="system-playlist-full-view__loading-more"
                data-infinite-scroll-spinner="true"
              >
                <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
              </div>
            ) : null}
          </>
        ) : null}
        {!dataLoading && items.length === 0 ? (
          <div className="system-playlist-full-view__empty">
            <p>{emptyText}</p>
          </div>
        ) : null}
      </div>
  )
}

// ---------------------------------------------------------------------------
// Outer wrapper — installs the <StaticTextProvider> around Inner so its
// useTranslatedText hooks resolve against the `communityMyLibraryPage` slice
// the HOC injects. Mirrors the Oracle pattern (PlaylistSharedView →
// PlaylistSharedViewInner).
// ---------------------------------------------------------------------------

function SystemPlaylistFullViewContent ({
  staticText,
  ...rest
}: SystemPlaylistFullViewInnerProps) {
  return (
    <StaticTextProvider staticText={staticText}>
      <SystemPlaylistFullViewContentInner {...rest} />
    </StaticTextProvider>
  )
}

// ---------------------------------------------------------------------------
// HOC composition — staticText + page SEO (no Redux tiles, no languageConnect)
// ---------------------------------------------------------------------------

const ConnectedContent = compose(
  connectStaticText({ storeKey: 'communityMyLibraryPage' }),
  connectPage({
    seoType: PAGE_SEO_TYPE_LOCATION,
    storeBranch: '',
    storeKey: 'communityMyLibraryPage',
  }),
)(SystemPlaylistFullViewContent)

// ---------------------------------------------------------------------------
// Outer export
// ---------------------------------------------------------------------------

interface SystemPlaylistFullViewProps {
  history: { push: (path: string) => void }
  location: { pathname: string }
}

export default function SystemPlaylistFullView (
  props: SystemPlaylistFullViewProps,
) {
  return <ConnectedContent {...props} />
}
